import settings from '../settings.js'

const $inject = ['$ionicPopup', '$http', '$ionicLoading', 'UserService', '$rootScope', 'common']

function FitnessService(ionicPopup, http, ionicLoading, UserService, rootScope, common) {
    this.fitnessCategories = [];
    this.fitnessEvents = [];

    this.hasEventListPermission = false;

    this.changedFitnessEventActivityId = false;

    this.categoriesLoaded = false;
    this.eventsLoaded = false;

    this.lastDoorEntry = {
        lastRefreshed: null,
        lastEntryTime: null
    };

    this.fitnessAPI = () => {
        return {
            categoriesLoaded: this.categoriesLoaded,
            eventsLoaded: this.eventsLoaded,
            fitnessCategories: this.fitnessCategories,
            fitnessEvents: this.fitnessEvents,
            getActivityPersonsString: this.getActivityPersonsString,
            getTimeDisplayFormat: this.getTimeDisplayFormat,
            getTotalUserAttendanceQuantity: this.getTotalUserAttendanceQuantity
        };
    };

    this.getAllLoaded = () => (this.categoriesLoaded && this.eventsLoaded);
    this.getFitnessCategories = () => this.fitnessCategories;

    this.getFitnessEvents = (activityId) => {
        if (!activityId)
            return this.fitnessEvents;

        return (this.fitnessEvents && this.fitnessEvents.find((event) => event.activityId === activityId)) || false;
    };

    //Get total quantity of events logged user is attending. Not queues.
    this.getTotalUserAttendanceQuantity = (event) => {
        if (!event.eventAttendees)
            return 0;

        return event.eventAttendees.reduce((totalQty, eventAttendee) => {
            return (eventAttendee.personId === UserService.user.personId) ? totalQty + eventAttendee.quantity : totalQty
        }, 0);
    }

    //0 = Not in attendance
    //1 = In attendance but not registered
    //2 = In attendance and registered in
    //3 = In queue waiting for open slot
    this.getFitnessStatus = (event) => {
        if (!event)
            return false;

        //This supports only one attendance row per person, since findIndex always finds first element
        const isPersonInAttendance = event.eventAttendees && event.eventAttendees.findIndex((attendee) => attendee.personId === UserService.user.personId && attendee.type === 1) > -1;
        const isPersonAttendeeRegistered = event.eventAttendees && event.eventAttendees.findIndex((attendee) => attendee.personId === UserService.user.personId && attendee.type === 1 && attendee.registered) > -1;
        const isPersonInQueue = event.eventAttendees && event.eventAttendees.findIndex((attendee) => attendee.personId === UserService.user.personId && attendee.type === 3) > -1;

        return (isPersonInAttendance ? (isPersonAttendeeRegistered ? 2 : 1) : (isPersonInQueue ? 3 : 0));
    };

    //Get string of all activityPersons separated with comma
    this.getActivityPersonsString = (event) => {
        if (!event || !event.activityPersons)
            return "";

        return event.activityPersons.reduce((retStr, activityPerson) => retStr + activityPerson.firstName + ", ", '').slice(0, -2);
    };

    //Handle date/time formatting
    this.getTimeDisplayFormat = (event, startOnly = false) => {
        if (!event)
            return "";

        const activityStartMoment = moment(event.activityStartTime, 'DD.MM.YYYY HH:mm');
        const activityEndMoment = moment(event.activityEndTime, 'DD.MM.YYYY HH:mm');

        let clockString;

        if (UserService.langCode === 'en-GB') {
            clockString = ' at ';
            activityStartMoment.locale('en');
        }
        else if (UserService.langCode === 'sv-SE') {
            clockString = ' klockan ';
            activityStartMoment.locale('sv');
        }
        else if (UserService.langCode === 'de-DE') {
            clockString = ' bei ';
            activityStartMoment.locale('de');
        }        
        else {
            clockString = ' klo ';
            activityStartMoment.locale('fi');
        }

        const returnString = activityStartMoment.format('ddd L').ucfirst() + clockString + activityStartMoment.format('HH:mm');

        return startOnly ? returnString : (returnString + ' - ' + activityEndMoment.format('HH:mm'));
    };

    //Get time when user last opened a door. 
    this.refreshLastEntryTime = () => {
        return new Promise((resolve, reject) => {
            const successHandler = (response) => {
                if (response.lastEntryTime) {
                    this.lastDoorEntry.lastEntryTime = moment(response.data.lastEntryTime, 'YYYY-MM-DD HH:mm:ss');
                    this.lastDoorEntry.lastRefreshed = moment();
                }

                resolve(true);
            }

            http({
                method: 'GET',
                url: settings.baseUrl + '?controller=ajax&getlastdoorentry=1' + '&appauth=' + settings.appAuthSecret
            }).then(successHandler).catch(reject)
        });
    };
    
    //Is user able to confirm fitness events? Logged in while within valid location, or opened a door within lastDoorEntry.lastEntryTime
    this.getUserCanConfirm = () => {
        if (!settings.fitnessConfirmationEnabled || !UserService.user.personId || UserService.loginData.loginState !== 2)
            return false;

        //Local can always confirm
        if (settings.useLocalFitness)
            return true;

        //If we don't require location check, logged in user can always confirm (within confirmable minutes anyway)
        if (typeof settings.eventConfirm.requireLocationCheck !== 'undefined' && !settings.eventConfirm.requireLocationCheck)
            return true;

        //Can confirm if opened door within last X minutes. For Beacon opens.
        return moment.isMoment(this.lastDoorEntry.lastEntryTime) && moment().utcOffset(0, true).diff(this.lastDoorEntry.lastEntryTime.utcOffset(0, true), 'minutes') <= settings.eventConfirm.ifDoorOpenedInLastMinutes;
    };

    //Users can begin to confirm event attendance startBeforeMinutes before event begins.
    this.isEventConfirmable = (event, isAutoConfirmCheck = false) => {
        const untilStartMinutes = moment(event.activityStartTime, 'DD.MM.YYYY HH:mm').utcOffset(0, true).diff(moment().utcOffset(0, true), 'minutes');
        const untilEndMinutes = moment(event.activityEndTime, 'DD.MM.YYYY HH:mm').utcOffset(0, true).diff(moment().utcOffset(0, true), 'minutes');

        if (isAutoConfirmCheck) {
            if (settings.eventConfirm.autoConfirmWithinMinutes === false)
                return false;

            if (settings.eventConfirm.autoConfirmWithinMinutes !== true)
                return untilStartMinutes <= settings.eventConfirm.autoConfirmWithinMinutes && untilEndMinutes > 0;
        }

        return untilStartMinutes <= settings.eventConfirm.startBeforeMinutes && untilEndMinutes > 0;
    }

    //Return true if enrollment currently open, false if enrollment already over, datetime string if enrollment opens in the future.
    this.isEventEnrollable = (event) => {
        const enrollmentStartTimeMoment = event.enrollmentStartTime ? moment(event.enrollmentStartTime).utcOffset(0, true) : false;

        //If enrollment start date is set, it override everything.
        if (enrollmentStartTimeMoment) {
            if (moment().utcOffset(0, true).isSameOrAfter(enrollmentStartTimeMoment, "minute"))
                return true;

            return enrollmentStartTimeMoment.format('DD.MM.YYYY HH:mm');
        }

        const untilStartMinutes = moment(event.activityStartTime, 'DD.MM.YYYY HH:mm').utcOffset(0, true).diff(moment().utcOffset(0, true), 'minutes');
        const untilEndMinutes = moment(event.activityEndTime, 'DD.MM.YYYY HH:mm').utcOffset(0, true).diff(moment().utcOffset(0, true), 'minutes');

        //For some categories we check the activityEndTime instead of start.
        const canEnrollUntilEnd = (event.eventCategoryId === settings.enrollUntilEndCategoryId);        

        //Has event has already started, or already ended for those events where enrollment until end is allowed
        if ((!canEnrollUntilEnd && untilStartMinutes <= settings.eventEnroll.stopBeforeMinutes) || (canEnrollUntilEnd && untilEndMinutes < 0))
            return false;

        //If enrollment not open yet, show date when it will open.
        if (untilStartMinutes > settings.eventEnroll.startBeforeMinutes)
            return moment.parseZone(event.activityStartTime, 'DD.MM.YYYY HH:mm').subtract(settings.eventEnroll.startBeforeMinutes, 'minutes').format('DD.MM.YYYY HH:mm');

        //Enrollment open
        return true;
    };

    //Check if user can cancel an enrolled or confirmed event
    this.isEventCancellable = (event) => { 
        const untilStartMinutes = moment(event.activityStartTime, 'DD.MM.YYYY HH:mm').utcOffset(0, true).diff(moment().utcOffset(0, true), 'minutes');
        const fitnessStatus = this.getFitnessStatus(event);
        const allowEnrollConfirmCancelBeforeMinutes = typeof settings.allowEnrollConfirmCancelBeforeMinutes === 'undefined' ? true : settings.allowEnrollConfirmCancelBeforeMinutes;

        //allowEnrollConfirmCancelBeforeMinutes of false means cancel not allowed. true means cancel allowed. Number means allowed until less than that many minutes to start.
        //This applies to enroll and confirmed (if allowConfirmCancel is true) both. Queue cancelling is not affected by this.
        if ([1, 2].indexOf(fitnessStatus) !== -1 && (allowEnrollConfirmCancelBeforeMinutes === false || (allowEnrollConfirmCancelBeforeMinutes !== true && untilStartMinutes <= settings.allowEnrollConfirmCancelBeforeMinutes)))
            return false;
      
        //If confirmed, can only cancel if allowConfirmCancel is set
        if (fitnessStatus === 2 && !settings.eventConfirm.allowConfirmCancel)
            return false;

        //Can cancel
        return true;
    };

    //Which events can currently be confirmed?
    //If singleEvent is given, we will check ONLY that. Otherwise, we will look for confirmable in every fitness event
    this.getConfirmableEvents = (singleEvent = false) => {
        //User must be logged in
        if (!UserService.user.personId || UserService.loginData.loginState !== 2)
            return [];

        //Callback that checks if specific event is confirmable.
        const checkEventConfirmable = (event) => {
            //Find events with start times within confirmable minutes for which the current user has marked as attending, but which have not yet been confirmed.
            if (this.isEventConfirmable(event, false) && event.eventAttendees && event.eventAttendees.length)
                return event.eventAttendees.findIndex((attendee) => attendee.personId === UserService.user.personId && !attendee.registered) > -1;

            return false;
        }

        //Check single event only. Return event as array with key 0 if confirmable.
        if (singleEvent)
            return checkEventConfirmable(singleEvent) ? [singleEvent] : [];

        //Check all events. Return all confirmable events as array
        if (this.fitnessEvents)
            return this.fitnessEvents.filter((event) => checkEventConfirmable(event));

        return [];
    };

    //Fitness instructor have an extra tab in fitness showEvent where they can mark that a specific person is in attendance. This sets CrmEventsAttendees.markedAttendance
    this.markAttendance = (activityId, attendeeId, markedAttendance) => {
        return new Promise((resolve, reject) => {
            const successHandler = (response) => {
                if (response.data.success) {
                    const fitnessIndex = this.fitnessEvents.findIndex((evnt) => evnt.activityId === activityId);                    
                    const attendeeIndex = (fitnessIndex > -1 && Array.isArray(this.fitnessEvents[fitnessIndex].eventAttendees)) ? this.fitnessEvents[fitnessIndex].eventAttendees.findIndex((att) => att.attendeeId === attendeeId) : -1;

                    if (fitnessIndex > -1 && attendeeIndex > -1) {
                        this.fitnessEvents[fitnessIndex].eventAttendees[attendeeIndex].markedAttendance = markedAttendance;
                        return resolve(true);
                    }

                    return reject({error: 'No matching attendeeId found in fitnessEvents'});
                }

                return reject({error: response.data.error ? response.data.error : 'Tuntematon virhe'});
            };

            http({
                method: 'POST',
                url: settings.baseUrl + '?controller=ajax&markattendance=1&appauth=' + settings.appAuthSecret,
                data: {
                    attendeeId: attendeeId,
                    markedAttendance: markedAttendance
                }
            }).then(successHandler).catch((response) => reject(response.data));
        });
    };

    //Handle attend event button click. Can be attend, attendregister or cancel.
    this.handleEvent = (action, activityId) => {
        return new Promise((resolve, reject) => {
            console.log(`[${performance.now()}] Fitness handleEvent() called with action '${action ? action : "EMPTY"}' and activityId ${activityId ? activityId : "EMPTY"}.`);

            if (!activityId || !this.fitnessEvents)
                return resolve(false);

            const selectedActivityIndex = this.fitnessEvents.findIndex((evnt) => evnt.activityId === activityId);
            const event = selectedActivityIndex > -1 ? this.fitnessEvents[selectedActivityIndex] : false;

            if (!event)
                return resolve(false);

            ionicLoading.show({
                template: '<p>' + _locale('processing').ucfirst() + '...</p><ion-spinner></ion-spinner>'
            });

            const successHandler = (response) => {
                ionicLoading.hide();

                if (response.data.success && response.data.event) {
                    //Special handling for local Jumppakalenteri
                    if (settings.useLocalFitness && !settings.showLocalEventAttendConfirmPopup) {
                        //Logout immediately on successfull attend/confirm if we don't show showLocalEventAttendConfirmPopup
                        return rootScope.logout();
                    }

                    if (['attend', 'attendregister', 'cancel', 'joinqueue'].indexOf(action) > -1)
                        this.changedFitnessEventActivityId = activityId;

                    this.fitnessEvents[selectedActivityIndex] = response.data.event;

                    const popupPromise = ionicPopup.alert({
                        title: _locale('the operation was successful').ucfirst(),
                        template: _locale(response.data.success).ucfirst(),
						cssClass: 'bigger-popup' 
                    });


                    common.ionicPopupHandler(popupPromise, 3000);

                    //If watch does logout, close immediately.
                    rootScope.$on('User:loggedOut', () => {
                        popupPromise.close();
                    });

                    //Special handling for local Jumppakalenteri
                    if (settings.useLocalFitness) {
                        popupPromise.finally(() => {
                            //Check if we have already logged out earlier?
                            if (UserService.loginData.loginState !== 0)
                                return rootScope.logout();
                        });
                    }

					return resolve(true);
	            }
                const popupPromise = ionicPopup.alert({
                    title: _locale('the operation was unsuccessful').ucfirst(),
                    template: response.data.error ? _locale(response.data.error).ucfirst() : _locale('unknown error').ucfirst(),
                    cssClass: 'bigger-popup'
                });

                common.ionicPopupHandler(popupPromise, 3000);
	        };

            const errorHandler = (error) => {
                ionicLoading.hide();

                const popupPromise = ionicPopup.alert({
                    title: _locale('the operation was unsuccessful').ucfirst(),
                    template: _locale('unknown error').ucfirst(),
                    cssClass: 'bigger-popup'
                });

	            common.ionicPopupHandler(popupPromise, 3000);
                return resolve(false);
            }

            http({
                method: 'POST',
                url: settings.baseUrl + '?controller=ajax&handleevent=1&appauth=' + settings.appAuthSecret,
                data: {
                    action: action,
                    activityId: event.activityId,
                    hash: event.hash,
                    personId: UserService.user.personId,
                    isMobile: false,
                    isUserLocal: settings.useLocalFitness,
                    tagData: UserService.user.tagData || false
                }
            }).then(successHandler).catch(errorHandler);
        });
    };

    //Get fitness events and fitness categories from server.
    this.getFitnessData = () => {
        return new Promise((resolve, reject) => {
            const successHandler = (response) => {
                this.fitnessEvents = response.data.events;
                this.eventsLoaded = true;

                this.hasEventListPermission = response.data.hasEventListPermission ? response.data.hasEventListPermission : false;

                this.fitnessCategories = response.data.categories;
                this.categoriesLoaded = true;

                resolve(true);
            };

            const filterByLocationId = (fitCalLocationId && fitCalLocationId != -1) ? fitCalLocationId : false;

            http({
                method: 'POST',
                url: settings.baseUrl + '?controller=ajax&fitnessdata=1&lang=' + UserService.langCode + '&appauth=' + settings.appAuthSecret,
                data: {
                    personId: UserService.user.personId, //TODO: Get from session
                    tagData: UserService.user.tagData,
                    filterByLocationId: filterByLocationId
                }
            }).then(successHandler).catch(reject)
        });
    };
}

FitnessService.$inject = $inject;

export default FitnessService